import { Check, Info, Warning } from '@mui/icons-material';
import { Chip } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShowAutocomplete__Show as Show } from '~/components/ShowAutocomplete';
import { ShowAutocomplete } from '~/components/ShowAutocomplete';
import { Tooltip } from '~/components/Tooltip';
import { ReadOnlyDataGrid, hiddenHeader } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { Link } from '~/components/link';
import { AuthorizedLink } from '~/components/links';
import { useLink } from '~/hooks/link';
import ActivateDeviceIllustration from '~/images/illustrations/activate-device.svg';
import type {
  NetworkEventTypeShows__DeviceGroup as DeviceGroup,
  NetworkEventTypeShows__EventType as EventType,
} from '../../queries/shows.generated';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={ActivateDeviceIllustration}
      description={t('pages.event_types.shows_tab.table.empty_state.description')}
      header={t('pages.event_types.shows_tab.table.empty_state.header')}
    />
  );
};

const ShowStatus = ({
  deviceGroup,
  eventType,
  showId,
}: {
  deviceGroup: DeviceGroup;
  eventType: EventType;
  showId: number | null;
}) => {
  const { t } = useTranslation();
  const defaultShow = deviceGroup.defaultShow;

  const title =
    showId !== -1
      ? t('pages.event_types.shows_tab.table.show_status_selected', {
          eventTypeName: eventType.name,
        })
      : defaultShow
      ? t('pages.event_types.shows_tab.table.show_status_default', {
          defaultShowName: defaultShow.name,
          eventTypeName: eventType.name,
        })
      : t('pages.event_types.shows_tab.table.show_status_none', {
          eventTypeName: eventType.name,
        });

  const icon =
    showId !== -1 ? (
      <Check className="icon" color="success" fontSize="medium" />
    ) : defaultShow ? (
      <Info className="icon" color="info" fontSize="medium" />
    ) : (
      <Warning className="icon" color="warning" fontSize="medium" />
    );

  return <Tooltip title={title}>{icon}</Tooltip>;
};

export interface DeviceGroupColumnProps {
  getShow: (id: number) => number | null;
  eventType: EventType;
  setShow: (id: number, value: Show | null) => void;
}

const useColumns = ({ getShow, eventType, setShow }: DeviceGroupColumnProps): GridColDef[] => {
  const { t } = useTranslation();
  const link = useLink();

  const getActiveShow = useCallback(
    (eventType: EventType, deviceGroupId: number) => {
      const show =
        eventType.showDeviceGroups?.find((x) => x.show.id === getShow(deviceGroupId))?.show ?? null;
      return show;
    },
    [getShow],
  );

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.device_group'),
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => (
          <AuthorizedLink authorized={row.canUpdate.value} entity={row} />
        ),
        flex: 1,
      },
      {
        field: 'deviceCount',
        headerName: t('common.labels.devices'),
        headerAlign: 'center',
        align: 'center',
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return (
            <Chip
              color="primary"
              component={Link}
              label={row.deviceCount || 0}
              onClick={(event) => event.stopPropagation()}
              to={link(`/device-groups/${row.id}/devices`)}
            />
          );
        },
        minWidth: 250,
      },
      {
        field: 'status',
        ...hiddenHeader(t('common.labels.status')),
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return <ShowStatus deviceGroup={row} eventType={eventType} showId={getShow(row.id)} />;
        },
        maxWidth: 50,
      },
      {
        field: 'id',
        headerName: t('pages.event_types.shows_tab.table.columns.assigned_show'),
        flex: 0.5,
        renderCell: ({ row }: GridRenderCellParams<DeviceGroup>) => {
          return (
            <ShowAutocomplete
              onChange={(newValue: Show | null) => setShow(row.id, newValue)}
              value={getActiveShow(eventType, row.id)}
              size="small"
              sx={{
                flexBasis: '100%',
                display: 'inline-flex',
              }}
            />
          );
        },
      },
    ],
    [link, eventType, getShow, getActiveShow, setShow, t],
  );
};

export interface DeviceGroupTableProps {
  deviceGroups: readonly DeviceGroup[];
  eventType: EventType;
  getShow: (id: number) => number | null;
  loading: boolean;
  setShow: (id: number, value: Show | null) => void;
}

export const DeviceGroupTable = ({
  deviceGroups,
  eventType,
  getShow,
  loading,
  setShow,
}: DeviceGroupTableProps) => {
  const columns = useColumns({ getShow, eventType, setShow });
  return (
    <ReadOnlyDataGrid
      columns={columns}
      loading={loading}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      rows={deviceGroups}
    />
  );
};
